import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { lionSelectors } from '@rosetta/react-lion';
import { authActions, authSelectors, LOGIN_STATUS } from '@rosetta/react-auth';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { appActions, appSelectors } from '../../app/reducer/app-reducer';
import { passwordChangeActions } from '../reducer/password-change-reducer';
import {
  appMainBackground,
  defaultBoxShadow,
  warningRedColor,
  white
} from '../../../styles/colors';
import { parse } from 'query-string';
import { ssoLoginActions } from '../reducer/sso-login-reducer';
import { LaunchpadButton } from '../../../styles/LaunchpadButton';
import { LinkButton } from '../../../styles/LinkButton';
import { validateUserInfo } from '../../app/util/user-helper';
import { InputWithIconContainer as InputWithIcon } from '../../app/util/InputWithIcon';
import { InputAndAlertContainerContainer as InputAndAlertContainer } from '../../app/util/InputAndAlertContainer';
import { launchAppWithKey } from '../../../helpers/productConfigHelper';
import EndInteraction from '../../app/components/EndInteraction';

import { withWindowDimensions } from '@rosetta/react-common-ui';
import { isMobile as isMobileDetected } from 'react-device-detect';

const LoginDiv = styled.div((props) => {
  let style = {
    position: !props.isMobile ? 'absolute' : 'relative',
    top: '0px',
    bottom: '0px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appMainBackground
  };
  if (props.isMobile) {
    style.minHeight = props.windowHeight - 250 + 'px';
  }
  return style;
});

let boxWidth = 340;
let boxSidePadding = 22;

const LoginBox = styled.div((props) => {
  let style = {
    display: 'block',
    backgroundColor: !props.isMobile ? white : appMainBackground,
    paddingBottom: '32px',
    width: '100%',
    maxWidth: !props.isMobile ? boxWidth : '400px',
    boxShadow: !props.isMobile ? defaultBoxShadow : 'none',
    borderRadius: '4px'
  };
  return style;
});

const WelcomeText = styled.div(() => {
  let style = {
    width: '100%',
    fontSize: '20px',
    lineHeight: '33px',
    textAlign: 'center',
    fontWeight: '200',
    letterSpacing: '1px',
    marginBottom: '31px',
    paddingTop: '22px',
    paddingLeft: boxSidePadding,
    paddingRight: boxSidePadding,
    paddingBottom: '12px',
    borderBottomColor: appMainBackground,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid'
  };
  return style;
});

const ErrorContainer = styled.div(() => {
  let style = {
    color: warningRedColor,
    fontSize: '12px',
    marginTop: 1,
    marginBottom: 12,
    paddingLeft: boxSidePadding,
    paddingRight: boxSidePadding,
    width: '100%'
  };
  return style;
});

const PasswordInputContainer = styled.div(() => {
  let style = {
    width: '100%',
    paddingLeft: boxSidePadding,
    paddingRight: boxSidePadding,
    marginBottom: '35px'
  };
  return style;
});

const ButtonsContainer = styled.div(() => {
  let style = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'spaceBetween',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding
  };
  return style;
});

const LoginButton = styled(LaunchpadButton)((props) => {
  let style = {
    fontSize: 16
  };
  if (props.isMobile) {
    style.borderRadius = '4px';
  }
  return style;
});

const ForgotPasswordButton = styled(LinkButton)((props) => {
  let style = {
    marginTop: '28px',
    fontSize: !props.isMobile ? '14px' : '12px',
    textShadow: '2px 2px 6px rgba(0,0,0,0.1)'
  };
  return style;
});

//*********************************************
export class Login extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showLogin: true,
      validatingEmail: false,
      tempPassword: '',
      tempUsername: '',
      redirectParams: '',
      redirecting: false
    };
    // If the token is provided on the url, this is a SAML SSO flow
    const queryParams = parse(this.props.location.search);
    if (queryParams.rst_access_token) {
      this.state.showLogin = false;
      this.props.routeSsoFlow(queryParams);
    }
    // check for redirect query params, if provided, save in component state for use after authentication
    if (queryParams.redirect_uri) {
      // capture redirect hash before it's lost as it's not parsed into queryParams
      const location_hash = window.location.hash;
      const redirect_uri_param_name = 'redirect_uri=';
      const redirect_uri_with_hash = location_hash.slice(location_hash.toLowerCase().indexOf(redirect_uri_param_name));
      queryParams.redirect_uri = decodeURIComponent(redirect_uri_with_hash.slice(redirect_uri_param_name.length));
      this.state.redirectParams = queryParams;
    }
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    if (nextProps.loginStatus === LOGIN_STATUS.LOGGED_IN && !prevState.redirecting) {
      if (prevState.redirectParams) {
        // user initially landed on #/redirect path, now that they're logged in, proceed with the redirect
        const redirectParams = prevState.redirectParams;

        if (redirectParams.client_id && redirectParams.redirect_uri && redirectParams.response_type === 'code') {
          // convert "Product:GAIA" to "GAIA" for backward compatibility (See RSMC-13393)
          let productKey = redirectParams.client_id;
          const productString = 'Product:';
          if (productKey.indexOf(productString) >= 0) {
            productKey = productKey.slice(productKey.indexOf(productString) + productString.length);
          }

          if (launchAppWithKey(productKey, redirectParams.redirect_uri)) {
            return { redirecting: true, redirectParams: '' };
          }
          // if app fails to launch, we'll fall through to the redirect to the LP home page below
        }
      }
    }

    if (nextProps.loginStatus === LOGIN_STATUS.LOGGED_IN && !prevState.redirecting) {
      const launchpadUri = window.location.href.replace(window.location.hash, '#/launchpad');
      window.location = launchpadUri;
      return { redirecting: true };
    }

    return null;
  };

  handleUsernameChange = (event) => {
    this.props.clearAuthError();
    this.setState({ tempUsername: event.target.value });
  };

  // the username aka email is validated when the user clicks outside the input,
  //  but only after they leave it once with it non-empty
  handleUsernameBlur = () => {
    if (this.state.tempUsername.length > 0) {
      this.setState({
        validatingEmail: true
      });
    }
  };

  emailError = () => {
    return validateUserInfo('email', this.state.tempUsername);
  };

  handlePasswordChange = (event) => {
    this.props.clearAuthError();
    this.setState({ tempPassword: event.target.value });
  };

  handleLoginButtonClick = () => {
    if (!this.isSubmitDisabled()) {
      this.props.loginUser(this.state.tempUsername, this.state.tempPassword, '');
    }
  };

  handleForgotPasswordClick = (event) => {
    this.props.goToForgotPassword(this.state.tempUsername);
  };

  mapEnterToAction = (event) => {
    if (event.key === 'Enter') {
      this.handleLoginButtonClick();
    }
  };

  isSubmitDisabled = () => {
    return this.props.authErrorMsg.length > 0 || this.emailError().length > 0 || this.state.tempPassword.length < 6;
  };

  // ****** render
  render = () => {
    const submitDisabled = this.props.authErrorMsg.length > 0 || this.emailError().length > 0 || this.state.tempPassword.length < 6;

    if (!this.props.isAppInitialized || !this.state.showLogin) {
      return <MouseCatcher />;
    }

    if (!this.props.lionLoaded) {
      return <MouseCatcher />;
    }

    if (this.state.redirecting) {
      return <MouseCatcher />;
    }

    const isMobile = isMobileDetected;
    let errorDisp = '';
    let errorKey = '';
    let hasEmailError = false;
    if (this.props.authErrorMsg) {
      errorKey = this.props.authErrorMsg;
    } else if (this.state.validatingEmail) {
      errorKey = this.emailError();
      hasEmailError = errorKey.length > 0;
    }
    if (errorKey.length > 0) {
      let errorString = this.props.lionTranslateString(errorKey);
      if (errorString === errorKey) {
        errorString = this.props.lionTranslateString('error_internal_exception');
      }
      errorDisp = (
        <ErrorContainer data-qa="ErrorContainer">
          <div data-qa={errorKey}>{errorString}</div>
        </ErrorContainer>
      );
    }

    let welcomeTextKey = 'login_welcome';

    const loginButton = (
      <LoginButton
        data-qa="SignInButton"
        onClick={this.handleLoginButtonClick}
        disabled={submitDisabled}
        tabIndex={3}
        onKeyPress={this.handleLoginButtonClick}
        isMobile={isMobile}
      >
        {this.props.lionTranslateString('login_button_signin')}
      </LoginButton>
    );

    const forgotPasswordButton = (
      <ForgotPasswordButton data-qa="ForgotPasswordLink" onClick={this.handleForgotPasswordClick} isMobile={isMobile}>
        {this.props.lionTranslateString('login_forgot_password_link')}
      </ForgotPasswordButton>
    );

    let buttonBlock = (
      <ButtonsContainer>
        {loginButton} {forgotPasswordButton}
      </ButtonsContainer>
    );

    const mouseCatcher = this.props.loginStatus === LOGIN_STATUS.PENDING ? <MouseCatcher loginCatcher /> : null;

    const inputHeight = 40;

    return (
      <LoginDiv data-qa="Login" isMobile={isMobile} windowHeight={this.props.clientHeight}>
        {/* event will be sent after user authenticates */}
        <EndInteraction interaction="application-startup" />
        <LoginBox isMobile={isMobile}>
          <WelcomeText
            data-qa="WelcomeText"
            dangerouslySetInnerHTML={{
              __html: this.props.lionTranslateString(welcomeTextKey)
            }}
          />
          {errorDisp}
          <InputAndAlertContainer
            dataQa="EmailInputAndAlertContainer"
            displayError={hasEmailError}
            outsideHeight={inputHeight}
            inputSideMargin={boxSidePadding}
          >
            <InputWithIcon
              inputType="text"
              dataQa="Email"
              placeholder={this.props.lionTranslateString('login_email_field')}
              value={this.state.tempUsername}
              autoFocus={true}
              tabIndex={1}
              onKeyPress={this.mapEnterToAction}
              onChange={this.handleUsernameChange}
              onBlur={this.handleUsernameBlur}
              outsideHeight={inputHeight}
              displayError={hasEmailError}
            />
          </InputAndAlertContainer>
          <PasswordInputContainer>
            <InputWithIcon
              inputType="password"
              dataQa="Password"
              placeholder={this.props.lionTranslateString('login_password_field')}
              tabIndex={2}
              value={this.state.tempPassword}
              onKeyPress={this.mapEnterToAction}
              onChange={this.handlePasswordChange}
              outsideHeight={inputHeight}
            />
          </PasswordInputContainer>
          {buttonBlock}
        </LoginBox>
        {mouseCatcher}
      </LoginDiv>
    );
  };
}

Login.propTypes = {};

const mapStateToProps = (state, ownprops) => ({
  isAppInitialized: appSelectors.isAppInitialized(state),
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  lionTranslateString: (key, placeholders, values) => {
    return lionSelectors.getLionTranslatedString(state, key, placeholders, values);
  },
  loginStatus: authSelectors.loginStatus(state),
  authErrorMsg: authSelectors.errorMsg(state)
});

const wrapActions = {
  loginUser: appActions.loginUser,
  clearAuthError: authActions.clearAuthError,
  routeSsoFlow: ssoLoginActions.routeSsoFlow,
  goToForgotPassword: passwordChangeActions.goToForgotPassword
};
export const LoginContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(Login));



// WEBPACK FOOTER //
// ./src/modules/login/components/Login.jsx